/**
 * Boletra · @boletra/i18n
 *
 * Configuración compartida de i18n para web, scanner y API.
 * Usa i18next + ICU MessageFormat. Para agregar un idioma nuevo:
 *   1. Duplicar `locales/es/` a `locales/[lang]/` y traducir.
 *   2. Agregar la clave a `SUPPORTED_LOCALES`.
 *   3. Re-correr `pnpm i18n:typegen`.
 */

export const SUPPORTED_LOCALES = ['es', 'en'] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];

export const DEFAULT_LOCALE: Locale = 'es';
export const FALLBACK_LOCALE: Locale = 'en';

export const NAMESPACES = [
  'common',
  'auth',
  'event',
  'dashboard',
  'scanner',
  'emails',
  'errors',
  'landing',
  'discover',
  'legal',
] as const;
export type Namespace = (typeof NAMESPACES)[number];

export type LocaleResources = {
  [K in Namespace]: Record<string, unknown>;
};

/**
 * Carga sincrónica de los recursos. En el cliente Vite/Expo usa
 * import.meta.glob; en Node se carga desde disco. Esta función es
 * el shape que ambos resuelven.
 */
export type LoadLocale = (locale: Locale) => Promise<LocaleResources>;

export const isSupportedLocale = (value: unknown): value is Locale =>
  typeof value === 'string' && (SUPPORTED_LOCALES as readonly string[]).includes(value);

export const normalizeLocale = (value: string | null | undefined): Locale => {
  if (!value) return DEFAULT_LOCALE;
  const lower = value.toLowerCase().split('-')[0];
  return isSupportedLocale(lower) ? lower : DEFAULT_LOCALE;
};

/**
 * Format helpers — wrappers thin de Intl que respetan el locale del usuario.
 * Para textos con plurales/monedas dentro de mensajes, usar ICU directo en JSON:
 *   "tickets_count": "{count, plural, one {# ticket} other {# tickets}}"
 *   "price": "{amount, number, ::currency/USD}"
 */

export function formatCurrency(
  amount: number,
  currency: string,
  locale: Locale = DEFAULT_LOCALE,
): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: 2,
  }).format(amount);
}

export function formatDate(
  date: Date | string | number,
  locale: Locale = DEFAULT_LOCALE,
  options: Intl.DateTimeFormatOptions = { dateStyle: 'long', timeStyle: 'short' },
): string {
  return new Intl.DateTimeFormat(locale, options).format(new Date(date));
}

export function formatNumber(value: number, locale: Locale = DEFAULT_LOCALE): string {
  return new Intl.NumberFormat(locale).format(value);
}

export function formatRelativeTime(
  fromDate: Date | string | number,
  locale: Locale = DEFAULT_LOCALE,
): string {
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  const diffMs = new Date(fromDate).getTime() - Date.now();
  const diffSec = Math.round(diffMs / 1000);
  const abs = Math.abs(diffSec);
  if (abs < 60) return rtf.format(diffSec, 'second');
  if (abs < 3600) return rtf.format(Math.round(diffSec / 60), 'minute');
  if (abs < 86_400) return rtf.format(Math.round(diffSec / 3600), 'hour');
  return rtf.format(Math.round(diffSec / 86_400), 'day');
}

/**
 * Locale display names (usado en el switcher del UI).
 */
export const LOCALE_LABELS: Record<Locale, { native: string; en: string; es: string }> = {
  es: { native: 'Español', en: 'Spanish', es: 'Español' },
  en: { native: 'English', en: 'English', es: 'Inglés' },
};
